import React, { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import MaterialModal from "../MaterialModal";
import { useDropzone } from "react-dropzone";
import Alert from "@material-ui/lab/Alert";
import Moment from "moment";
import { hideModal } from "../../../actions/actionCreators";
import { GetS3FileList, GetS3FileInfo, ResetS3Data } from "../../../actions/awsActions";
import { notifyError } from "../../../actions/notifyActions";
import TabPanel, { a11yProps } from "../../material-components/Misc/TabPanel";
import withModal from "../withModal";
import LoadingDots from "../../loading/BouncingDots";
import { makeStyles } from "@material-ui/core/styles";
import {
    Tabs,
    Tab,
    Divider,
    List,
    ListItem,
    ListItemText,
    TextareaAutosize,
    IconButton,
    Checkbox,
    FormControlLabel,
    TextField,
    Button,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { IAppState } from "../../../types/stores/index";
import { getFlowExternalServiceInputsForSelectedFlowItem } from "../../../reducers/flowExternalServiceInputs";
import { IFileInfo } from "../../../types/actions/fileInfo";
import { IS3File } from "../../../types/actions/aws";
import { getDeploymentDestinationServers } from "../../../actions/DeploymentDestinationActions";
import { IDeploymentDestinationServer } from "../../../types/stores/deploymentDestination";
import CircularLoading from "../../loading/CircularLoading";
import {
    GetNasFileInfo,
    RemoveNasFilesDownloadQueue,
    ResetNasData,
    SetNasFilesList,
    searchNasFiles,
} from "../../../actions/nasActions";
import { endRequestProgress } from "../../../actions/loadingActions";

const FolderIcon = <i className="material-icons">folder</i>;
const CloseIcon = onClick => (
    <IconButton onClick={onClick.onClick} size="small">
        <i className="material-icons">close</i>
    </IconButton>
);

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        height: 224,
    },
    tabs: {
        borderRight: `1px solid ${theme.palette.divider}`,
        textAlign: "left",
    },
}));

export const BrowseFileIntegrations = {
    Computer: 0,
    S3: 1,
    SERVER: 2,
};

export interface IS3Input {
    id: number;
    name: string;
}

const BrowseFilesModal = ({ isOpen, modalProps }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const loadingPreviewLabel = "Loading...";

    // State

    // S3
    const [selectedS3Files, setSelectedS3Files] = useState<Array<IS3Input>>([]);
    // PC
    const [pcFilePreview, setPCFilePreview] = useState("");
    // Others
    const [tabValue, setTabValue] = useState(BrowseFileIntegrations.Computer);
    const [requestor, setRequestor] = useState("");
    // Server
    const [selectedNasServer, setSelectedNasServer] = useState<IDeploymentDestinationServer | null>(null);
    const [nasFilePath, setNasFilePath] = useState("");
    const [selectedNasFiles, setSelectedNasFiles] = useState<Array<string>>(new Array<string>());

    // Redux

    // S3
    const awsFilesList = useSelector((state: IAppState) => state.aws.files);
    const awsError = useSelector((state: IAppState) => state.aws.error);
    const awsIsLoadingFiles = useSelector((state: IAppState) => state.aws.isLoading);
    const awsIsDownloading = useSelector((state: IAppState) => state.aws.isDownloading);
    const awsFilePreview = useSelector((state: IAppState) => state.aws.filePreview);
    const awsFileQuantity = useSelector((state: IAppState) => state.aws.fileQuantity);
    const awsFilesInfo = useSelector<IAppState, Array<IFileInfo>>(state => state.aws.flowControlFilesInfo);

    // Others
    const enabledFeatures = useSelector((state: IAppState) => state.session.enabledFeatures || []);
    const flowExternalServiceInputs = useSelector(
        (state: IAppState) => getFlowExternalServiceInputsForSelectedFlowItem(state) || []
    );
    const roles = useSelector((state: IAppState) => state.session.roles);
    const isExperianOrSuperAdmin = roles.includes("Experian Admin") || roles.includes("Super Admin");
    const isInternal = useSelector((state: IAppState) => state.session.isInternal || false);

    // Deployment Destinations Servers
    const nasServers = useSelector((state: IAppState) => state.deploymentDestination.servers);
    const isNasServersLoading = useSelector((state: IAppState) => state.deploymentDestination.serversLoading);

    // NAS
    const isSearchingNasFiles = useSelector((state: IAppState) => state.nas.isLoading);
    const nasDownloadQueue = useSelector((state: IAppState) => state.nas.downloadQueue);
    const nasError = useSelector((state: IAppState) => state.nas.error);
    const nasFiles = useSelector((state: IAppState) => state.nas.files);
    const nasFilesInfo = useSelector((state: IAppState) => state.nas.filesInfo);

    // Features
    const flowControlFeature = enabledFeatures.includes("flow-control");
    const multiInputFeature = enabledFeatures.includes("flow-control-multi-input");

    // S3
    const s3Feature = enabledFeatures.includes("s3-file-import");
    const allowS3 =
        (requestor == "flow" && flowControlFeature && modalProps.browseFileAllowed == BrowseFileIntegrations.S3) ||
        (s3Feature && requestor != "flow");

    // Server
    const serverFeature = enabledFeatures.includes("flow-control-server-input");
    const allowNAS =
        serverFeature &&
        isExperianOrSuperAdmin &&
        isInternal &&
        requestor == "flow" &&
        flowControlFeature &&
        modalProps.browseFileAllowed == BrowseFileIntegrations.SERVER;

    useEffect(() => {
        if (modalProps.requestor && flowControlFeature) {
            setRequestor(modalProps.requestor);

            if (modalProps.requestor == "flow") {
                if (allowS3) {
                    setTabValue(BrowseFileIntegrations.S3);
                } else {
                    setTabValue(BrowseFileIntegrations.SERVER);
                    dispatch(getDeploymentDestinationServers());
                }
            }
        }
    }, [dispatch, modalProps, flowControlFeature, allowS3]);

    useEffect(() => {
        if (tabValue == BrowseFileIntegrations.S3) {
            dispatch(GetS3FileList());
        }
    }, [tabValue, dispatch]);

    // Modal options
    const hide = () => {
        setTabValue(BrowseFileIntegrations.Computer);
        setRequestor("");

        if (modalProps.browseFileAllowed == BrowseFileIntegrations.S3) {
            setSelectedS3Files([]);
            dispatch(ResetS3Data());
        } else if (modalProps.browseFileAllowed == BrowseFileIntegrations.SERVER) {
            setSelectedNasFiles([]);
            dispatch(ResetNasData());
        }

        dispatch(hideModal("BROWSE_FILES"));
    };

    const handleTabChange = (_, newValue) => {
        setSelectedS3Files([]);
        setSelectedNasFiles([]);
        setTabValue(newValue);
    };

    const handleSave = () => {
        if (tabValue == BrowseFileIntegrations.S3) {
            if (requestor == "flow" && multiInputFeature) {
                // FlowControl handles multiple files
                modalProps.handleModalFile(tabValue, selectedS3Files);
            } else {
                // File Import only handles single file
                modalProps.handleModalFile(tabValue, selectedS3Files[0], awsFilePreview, awsFileQuantity);
            }
            hide();
        } else if (tabValue == BrowseFileIntegrations.SERVER) {
            if (requestor == "flow" && multiInputFeature) {
                // FlowControl handles multiple files
                const selectedNasFilesInfo = nasFilesInfo.filter(x => selectedNasFiles.includes(x.fileName)) || [];
                modalProps.handleModalFile(tabValue, selectedNasFilesInfo, selectedNasServer?.Id);
            } else {
                // File Import only handles single file
                const fileInfo = nasFilesInfo.find(x => x.fileName == selectedNasFiles[0]);
                modalProps.handleModalFile(
                    tabValue,
                    fileInfo?.fileName,
                    fileInfo?.filePreview,
                    fileInfo?.fileQuantity,
                    selectedNasServer?.Id
                );
            }
            hide();
        } else {
            modalProps.handleModalFile(tabValue, selectedS3Files[0], pcFilePreview);
            hide();
        }
    };

    // PC files
    const readPCFile = useCallback(
        file => {
            const CHUNK_SIZE = 100000; // 100kb, arbitrarily chosen.
            const decoder = new TextDecoder();
            const maxLines = 10;
            let offset = 0;
            let lineCount = 0;
            let results: string | undefined = "";
            let preview = "";
            const fr = new FileReader();
            fr.onload = () => {
                // Use stream:true in case we cut the file
                // in the middle of a multi-byte character
                results += decoder.decode(fr.result as ArrayBuffer, { stream: true });
                const lines = (results && results.split("\n")) || [];
                results = lines.pop(); // In case the line did not end yet.
                lineCount += lines.length;

                if (lineCount > maxLines) {
                    // Read too many lines? Truncate the results.
                    lines.length -= lineCount - maxLines;
                    lineCount = maxLines;
                }

                for (let i = 0; i < lines.length; ++i) {
                    preview += lines[i] + "\n";
                }

                offset += CHUNK_SIZE;
                seek();
            };
            fr.onerror = () => {
                dispatch(notifyError("Error reading file: " + fr.error));
            };

            const seek = () => {
                if (lineCount === maxLines) {
                    // We found enough lines.
                    setPCFilePreview(preview);
                    return;
                }
                if (offset !== 0 && offset >= file.size) {
                    // We did not find all lines, but there are no more lines.
                    setPCFilePreview(preview);
                    return;
                }
                const slice = file.slice(offset, offset + CHUNK_SIZE);
                fr.readAsArrayBuffer(slice);
            };
            seek();
        },
        [dispatch]
    );

    const handlePCFileSelect = useCallback(
        selectedS3Files => {
            readPCFile(selectedS3Files[0]);
            setSelectedS3Files([selectedS3Files[0]]);
        },
        [readPCFile]
    );

    const onDrop = useCallback(
        acceptedFiles => {
            handlePCFileSelect([...acceptedFiles]);
        },
        [handlePCFileSelect]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: ".csv, .txt, .dat" });

    // S3 files
    const handleS3FileCheck = (file: IS3File, checked: boolean) => {
        if (checked) {
            handleS3FileSelect(file);
        } else {
            removeS3File(selectedS3Files.map(x => x.name).indexOf(file.Name));
        }
    };

    const handleS3FileSelect = (file: IS3File) => {
        if (!isS3FileSelected(file.Name)) {
            dispatch(GetS3FileInfo(file.Name));
            const nextId = selectedS3Files.length > 0 ? Math.max(...selectedS3Files.map(x => x.id)) + 1 : 1;
            const selectedFile: IS3Input = { id: nextId, name: file.Name };
            if (requestor == "flow" && multiInputFeature) {
                let updateSelected = [...selectedS3Files];
                updateSelected.push(selectedFile);
                setSelectedS3Files(updateSelected);
            } else {
                setSelectedS3Files([selectedFile]);
            }
        } else {
            removeS3File(selectedS3Files.map(x => x.name).indexOf(file.Name));
        }
    };

    const isS3FileSelected = (file: string) => selectedS3Files.map(x => x.name).includes(file);

    const removeS3File = fileIndex => {
        let filesCopy = [...selectedS3Files];
        if (fileIndex > -1) {
            filesCopy.splice(fileIndex, 1);
        }
        // reset id
        filesCopy.forEach((file, index) => {
            file.id = index + 1;
        });

        setSelectedS3Files(filesCopy);
    };

    // NAS files
    const handleNasServerChange = value => {
        setSelectedNasServer(value);
        setNasFilePath(value?.ServerPath ?? "");

        if (!value) {
            dispatch(SetNasFilesList([]));
            setSelectedNasFiles([]);
        }
    };

    const handleNasFilePathChange = event => {
        let value = event.target.value;
        if (value.startsWith(selectedNasServer?.ServerPath)) {
            setNasFilePath(value);
        } else {
            setNasFilePath(selectedNasServer?.ServerPath ?? "");
        }
    };

    const handleSearchNasFiles = () => {
        if (isSearchingNasFiles) {
            return;
        }

        if (selectedNasServer?.Id) {
            setSelectedNasFiles([]);
            dispatch(searchNasFiles(selectedNasServer.Id, nasFilePath));
        }
    };

    const isNasFileSelected = (file: string) => selectedNasFiles.map(x => x).includes(file);

    const handleNasFileCheck = (file: string, checked: boolean) => {
        if (checked) {
            handleNasFileSelect(file);
        } else {
            removeNasFile(selectedNasFiles.map(x => x).indexOf(file));
        }
    };

    const handleNasFileSelect = (file: string) => {
        if (!isNasFileSelected(file)) {
            if (selectedNasServer?.Id) {
                dispatch(GetNasFileInfo(selectedNasServer.Id, file));

                if (requestor == "flow" && multiInputFeature) {
                    let updateSelected = [...selectedNasFiles];
                    updateSelected.push(file);
                    setSelectedNasFiles(updateSelected);
                } else {
                    setSelectedNasFiles([file]);
                }
            }
        } else {
            removeNasFile(selectedNasFiles.map(x => x).indexOf(file));
        }
    };

    const removeNasFile = fileIndex => {
        let filesCopy = [...selectedNasFiles];
        if (fileIndex > -1) {
            const fileRemoved = filesCopy.splice(fileIndex, 1);

            if (nasDownloadQueue.length > 0 && !nasFilesInfo.some(x => x.fileName == fileRemoved[0])) {
                dispatch(RemoveNasFilesDownloadQueue(fileRemoved[0]));
                dispatch(endRequestProgress());
            }
        }

        setSelectedNasFiles(filesCopy);
    };

    // Shared functionality
    const isFileAdded = (file: string) => {
        if (requestor == "flow" && multiInputFeature) {
            return flowExternalServiceInputs.map(x => x.InputName).includes(file);
        }

        return false;
    };

    const getPreviewFiles = (source: number, fileName: string = "") => {
        let preview: string | undefined;

        if (source == BrowseFileIntegrations.S3) {
            preview =
                requestor == "flow" && multiInputFeature
                    ? awsFilesInfo.find(x => x.fileName == fileName)?.filePreview ?? loadingPreviewLabel
                    : awsFilePreview;
        } else if (source == BrowseFileIntegrations.SERVER) {
            preview = nasFilesInfo.find(x => x.fileName.endsWith(fileName))?.filePreview ?? loadingPreviewLabel;
        }

        return preview;
    };

    const displayPreviewFiles = (source: number) => {
        let isLoading =
            (source == BrowseFileIntegrations.S3 && awsIsDownloading) ||
            (source == BrowseFileIntegrations.SERVER && nasDownloadQueue.length > 0);

        const files =
            source == BrowseFileIntegrations.S3
                ? selectedS3Files.map(x => x.name)
                : selectedNasFiles.map(x => x.substring(x.lastIndexOf("\\") + 1));

        return (
            <div className="files-listing">
                {isLoading && (
                    <div style={{ margin: "10px 0", textAlign: "center" }}>
                        <LoadingDots />
                    </div>
                )}
                {files &&
                    files.length > 0 &&
                    files.map((file, i) => (
                        <div key={`div_${i}`}>
                            <Alert severity="success">
                                <span>
                                    <span>{file}</span>
                                    <CloseIcon
                                        onClick={() =>
                                            source == BrowseFileIntegrations.S3 ? removeS3File(i) : removeNasFile(i)
                                        }
                                    />
                                </span>
                            </Alert>
                            <TextareaAutosize
                                className="display-preview"
                                maxRows={4}
                                style={{
                                    width: "100%",
                                    height: "100px",
                                    resize: "none",
                                    overflow: "auto",
                                }}
                                value={getPreviewFiles(source, file)}
                                disabled
                            />
                        </div>
                    ))}
            </div>
        );
    };

    const displayOtherInfo = (sourceIntegrationName: string) => (
        <div className="browse-integration-desc-container">
            <span>Other information</span>
            <ol>
                <li>Currently, only accepts .csv, .txt, and .dat files.</li>
                <li>
                    Please contact your administrator for any assistance with setting up your {sourceIntegrationName}{" "}
                    integration.
                </li>
            </ol>
        </div>
    );

    return (
        <MaterialModal
            title={`Browse Files...`}
            isOpen={isOpen}
            onClose={hide}
            id="browse-files-modal"
            maxWidth="lg"
            onPrimaryButtonRequest={handleSave}
            primaryButtonText={"Add File"}
            isPrimaryButtonDisabled={
                (tabValue == BrowseFileIntegrations.S3 &&
                    (selectedS3Files.length <= 0 || awsError || awsIsLoadingFiles || awsIsDownloading)) ||
                (tabValue == BrowseFileIntegrations.SERVER &&
                    (selectedNasFiles.length <= 0 ||
                        nasError != null ||
                        isNasServersLoading ||
                        isSearchingNasFiles ||
                        nasDownloadQueue.length > 0))
            }
        >
            <div className={"row " + classes.root}>
                <div className="col col-xs-3 col-sm-2">
                    <Tabs
                        orientation="vertical"
                        variant="scrollable"
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="browse integrations"
                        className={classes.tabs}
                    >
                        <Tab
                            label="My Computer"
                            {...a11yProps(BrowseFileIntegrations.Computer)}
                            disabled={requestor == "flow" && flowControlFeature}
                        />
                        {s3Feature && (
                            <Tab label="Amazon S3" {...a11yProps(BrowseFileIntegrations.S3)} disabled={!allowS3} />
                        )}
                        {requestor == "flow" && serverFeature && isExperianOrSuperAdmin && isInternal && (
                            <Tab label="Server" {...a11yProps(BrowseFileIntegrations.SERVER)} disabled={!allowNAS} />
                        )}
                    </Tabs>
                </div>
                <div className="col col-xs-9 col-sm-10">
                    <TabPanel value={tabValue} index={BrowseFileIntegrations.Computer}>
                        <div className="my-computer-files-container">
                            <div {...getRootProps()} className="browse-drag-and-drop-container">
                                <input {...getInputProps()} className="browse-drag-and-drop-container-label" />
                                {isDragActive ? (
                                    <p>Drop the files here ...</p>
                                ) : (
                                    <>
                                        {FolderIcon}
                                        &nbsp;&nbsp; Drag & drop your files here or click here to browse on your
                                        computer ...
                                    </>
                                )}
                            </div>
                            <div className="files-listing">
                                {selectedS3Files &&
                                    selectedS3Files.length > 0 &&
                                    selectedS3Files.map((file, i) => (
                                        <div key={`div-${file.name}_${i}`}>
                                            <Alert severity="success" key={`alert-${file.name}_${i}`}>
                                                <span>
                                                    <span>{file.name}</span>
                                                    <CloseIcon onClick={() => removeS3File(i)} />
                                                </span>
                                            </Alert>
                                            <TextareaAutosize
                                                className="display-preview"
                                                maxRows={4}
                                                style={{
                                                    width: "100%",
                                                    height: "100px",
                                                    resize: "none",
                                                    overflow: "auto",
                                                }}
                                                value={pcFilePreview}
                                                disabled
                                            />
                                        </div>
                                    ))}
                            </div>
                            <Divider style={{ width: "95%" }} />
                            <div className="browse-integration-desc-container">
                                <span>Other information</span>
                                <ol>
                                    <li>Currently, only accepts .csv, .txt, and .dat files.</li>
                                    <li>Please contact your administrator for any assistance.</li>
                                </ol>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value={tabValue} index={BrowseFileIntegrations.S3}>
                        <div className="amazon-s3-files-container">
                            {!awsIsLoadingFiles && awsError && (
                                <Alert severity="warning" style={{ marginBottom: "10px" }}>
                                    {awsError}
                                </Alert>
                            )}
                            <div className="files-listing">
                                {awsIsLoadingFiles && (
                                    <div style={{ marginTop: "10px", textAlign: "center" }}>
                                        <LoadingDots />
                                    </div>
                                )}
                                {!awsIsLoadingFiles && !awsError && awsFilesList.length == 0 && (
                                    <p>Looks like there are no available files to import.</p>
                                )}
                                {!awsIsLoadingFiles && !awsError && awsFilesList.length > 0 && (
                                    <>
                                        <p className="modal-body-title">
                                            Available Files{" "}
                                            {requestor == "flow" && multiInputFeature && "(Select 1 to Many)"}
                                        </p>
                                        <List>
                                            {awsFilesList.map((file, i) => (
                                                <>
                                                    <div
                                                        key={`div-${i}`}
                                                        style={{ display: "flex", justifyContent: "start" }}
                                                    >
                                                        {requestor == "flow" && multiInputFeature && (
                                                            <FormControlLabel
                                                                key={`file-${i}`}
                                                                style={{ marginRight: "0px" }}
                                                                value={file.Name}
                                                                disabled={isFileAdded(file.Name)}
                                                                checked={isS3FileSelected(file.Name)}
                                                                control={
                                                                    <Checkbox
                                                                        key={`box-${i}`}
                                                                        color="primary"
                                                                        onChange={e =>
                                                                            handleS3FileCheck(file, e.target.checked)
                                                                        }
                                                                    />
                                                                }
                                                                label=""
                                                            />
                                                        )}
                                                        <ListItem
                                                            key={`li-${file.Name}--${i}`}
                                                            button
                                                            onClick={() => handleS3FileSelect(file)}
                                                            style={{ paddingTop: "0px", paddingBottom: "0px" }}
                                                            selected={isS3FileSelected(file.Name)}
                                                            disabled={isFileAdded(file.Name)}
                                                        >
                                                            <ListItemText
                                                                key={`lit-${file.Name}--${i}`}
                                                                primary={file.Name}
                                                                secondary={
                                                                    file.LastModified
                                                                        ? `Last Modified Date: ${Moment(
                                                                              file.LastModified
                                                                          ).format("MM/DD/YYYY HH:mm:ss A")}`
                                                                        : null
                                                                }
                                                            />
                                                        </ListItem>
                                                    </div>
                                                </>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </div>
                            {displayPreviewFiles(BrowseFileIntegrations.S3)}
                            <Divider style={{ width: "95%" }} />
                            {displayOtherInfo("Amazon S3")}
                        </div>
                    </TabPanel>
                    <TabPanel value={tabValue} index={BrowseFileIntegrations.SERVER}>
                        <div style={{ margin: "0 0 10px", width: "100%" }}>
                            <Autocomplete
                                id="nas-server-selection"
                                style={{ margin: "0 0 20px" }}
                                value={selectedNasServer}
                                autoComplete
                                fullWidth
                                options={nasServers}
                                getOptionLabel={option => option.Description}
                                onChange={(_, value) => handleNasServerChange(value)}
                                loading={isNasServersLoading}
                                loadingText={"Loading Servers..."}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Server"
                                        fullWidth
                                        InputProps={{ ...params.InputProps }}
                                    />
                                )}
                            />
                            <TextField
                                id="nas-server-file-path"
                                label="Folder/File Path"
                                helperText="Type the folder path or the full file path"
                                value={nasFilePath}
                                onChange={handleNasFilePathChange}
                                fullWidth
                                style={{ margin: "0 0 10px" }}
                                disabled={isNasServersLoading || selectedNasServer == null}
                            ></TextField>
                            <Button
                                id="search-nas-files-button"
                                variant="contained"
                                color="secondary"
                                className={"pull-right"}
                                style={{ minWidth: "fit-content" }}
                                onClick={() => handleSearchNasFiles()}
                                disabled={isNasServersLoading || selectedNasServer == null || nasFilePath == ""}
                            >
                                <div className="btn-saving-context" style={{ padding: "0px" }}>
                                    {isSearchingNasFiles ? (
                                        <CircularLoading size="x-small" color="#FFF" saved={false} />
                                    ) : null}
                                    <span>{isSearchingNasFiles ? "Searching..." : "Search"}</span>
                                </div>
                            </Button>
                            {!isNasServersLoading && nasError && (
                                <Alert severity="warning" style={{ margin: "50px 0 10px" }}>
                                    {nasError}
                                </Alert>
                            )}
                            <div className="files-listing">
                                {isNasServersLoading && (
                                    <div style={{ marginTop: "10px", textAlign: "center" }}>
                                        <LoadingDots />
                                    </div>
                                )}
                                {!isNasServersLoading && !nasError && nasFiles.length == 0 && (
                                    <p>Looks like there are no available files to import.</p>
                                )}
                                {!isNasServersLoading && !nasError && nasFiles.length > 0 && (
                                    <>
                                        <p className="modal-body-title">
                                            Available Files{" "}
                                            {requestor == "flow" && multiInputFeature && "(Select 1 to Many)"}
                                        </p>
                                        <List>
                                            {nasFiles.map((file, i) => (
                                                <>
                                                    <div
                                                        key={`div-${i}`}
                                                        style={{ display: "flex", justifyContent: "start" }}
                                                    >
                                                        {requestor == "flow" && multiInputFeature && (
                                                            <FormControlLabel
                                                                key={`file-${i}`}
                                                                style={{ marginRight: "0px" }}
                                                                value={file}
                                                                disabled={isFileAdded(file)}
                                                                checked={isNasFileSelected(file)}
                                                                control={
                                                                    <Checkbox
                                                                        key={`box-${i}`}
                                                                        color="primary"
                                                                        onChange={e =>
                                                                            handleNasFileCheck(file, e.target.checked)
                                                                        }
                                                                    />
                                                                }
                                                                label=""
                                                            />
                                                        )}
                                                        <ListItem
                                                            key={`li-file-${i}`}
                                                            button
                                                            onClick={() => handleNasFileSelect(file)}
                                                            style={{ paddingTop: "0px", paddingBottom: "0px" }}
                                                            selected={isNasFileSelected(file)}
                                                            disabled={isFileAdded(file)}
                                                        >
                                                            <ListItemText
                                                                key={`lit-file-${i}`}
                                                                primary={file.substring(file.lastIndexOf("\\") + 1)}
                                                                secondary={file}
                                                            />
                                                        </ListItem>
                                                    </div>
                                                </>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </div>
                            {!isNasServersLoading && !nasError && nasFiles.length > 0
                                ? displayPreviewFiles(BrowseFileIntegrations.SERVER)
                                : null}
                            <Divider style={{ width: "95%" }} />
                            {displayOtherInfo("SERVER")}
                        </div>
                    </TabPanel>
                </div>
            </div>
        </MaterialModal>
    );
};

BrowseFilesModal.propTypes = {
    modalProps: PropTypes.any,
    isOpen: PropTypes.bool,
};

BrowseFilesModal.displayName = "BrowseFilesModal";

export default withModal(BrowseFilesModal, "BROWSE_FILES");
